import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { usePolling } from '@/hooks/usePolling';
import { fetchBookOrder } from './api';
import type { BookOrderStatus, PreviewEnvelope } from './types';

/**
 * Non-terminal book states — Sebastian ruling 2026-08-12 evening
 * (widened from preview_rendering-only per Sebastian's Item 1
 * correction: story_generating alone lasted ~5 min on order 65c485ed
 * and was hand-reloaded through). Sebastian ruling 2026-08-28: also
 * widened to include anchor_approved — see below.
 *
 * These are the backend-driven transitions where the customer looks
 * at a wait / preview screen and needs auto-refresh:
 *
 *   light_gate_pending  → anchor_pending      (derivation success)
 *   anchor_pending      → anchor_review       (anchor render success)
 *   anchor_approved     → story_generating    (story processor picks
 *                                              the job off Redis —
 *                                              usually sub-second,
 *                                              can stretch during
 *                                              contention. Latent
 *                                              gap 2026-08-12 →
 *                                              2026-08-28: missing
 *                                              from this list AND
 *                                              from BookRoute switch;
 *                                              customer refetch after
 *                                              anchor-accept could
 *                                              land here and stick
 *                                              indefinitely because
 *                                              polling never
 *                                              restarted)
 *   story_generating    → text_editing        (Stage 1 first_pass:
 *                                              extraction + resolver
 *                                              + text-zone + assembly
 *                                              complete — the longest
 *                                              wait in the funnel)
 *   preview_rendering   → preview_ready       (all k preview renders
 *                                              settled)
 *
 * Customer-decision states (anchor_review, text_editing, preview_ready,
 * photo_rejected) are DELIBERATELY absent — those transition via a
 * PWA mutation (accept, edit, checkout, swap) which already calls
 * `refetch()` explicitly. Polling those wastes fetches on state that
 * only the customer can move.
 *
 * Post-payment happy-path states (paid, rendering_full, qa_running)
 * are INCLUDED — Sebastian ruling 2026-09-09 (Block 0 defect 0.2):
 * after paying, the customer sits on `PostPaymentWaitScreen` and
 * has to hand-reload to see the finished book. Polling these three
 * lets the screen auto-advance to `customer_review` (which renders
 * `ReviewScreen`) as soon as the backend flips the state. Note
 * that customer_review itself is customer-actionable and stays out
 * of the list. checkout_pending is also out — the customer is on
 * Stripe or on `BestellungScreen` (resume path), not on a wait
 * screen. qa_regen / qa_human_queue render a read-only info screen
 * (`BookStatusInfoScreen` with the "qaRegen" copy), no auto-
 * advance is expected there; if a future ruling widens the scope,
 * add them here.
 */
const NON_TERMINAL_POLLING_STATES: readonly BookOrderStatus[] = [
  'light_gate_pending',
  // Position-5 Commit 6b (Sebastian ruling 2026-09-02) — poll while
  // the customer is at summary_review so the first summary appears
  // without a manual refresh, and so a returning customer sees the
  // current runCount + generationFailed. The screen's dirty-state
  // guard suppresses re-render churn while the customer is typing a
  // change; polling is otherwise harmless (envelope shape stable).
  'summary_review',
  'anchor_pending',
  'anchor_approved',
  'story_generating',
  'preview_rendering',
  // Sebastian ruling 2026-09-09 (Block 0 defect 0.2) — post-payment
  // happy-path wait states. After paying, the customer stays on
  // PostPaymentWaitScreen until the backend flips the order to
  // customer_review; without polling these, the customer has to
  // hand-reload to see the finished book. paused_billing /
  // reclamation_open / cancelled and the print/ship/deliver chain
  // stay out — those are read-only info states, not wait states.
  'paid',
  'rendering_full',
  'qa_running',
  // Sebastian ruling 2026-09-08 evening (Commit 2 of A+B+C) —
  // release-chain terminal-fail state. Not customer-actionable: the
  // released copy says "wait for the resume mail." Included in
  // polling anyway so a customer who happens to keep the screen open
  // when the operator recovers the book (state flip back to
  // summary_review or anchor_pending) sees the next state without a
  // manual refresh. Cheap fetch, better UX for the returning-open-tab
  // case. Contrast photo_rejected/anchor_review/text_editing/
  // preview_ready which are deliberately absent because they
  // transition on a PWA mutation, not on backend forward progress.
  'book_release_chain_error',
];

/**
 * 5-second cadence — Sebastian ruling 2026-08-12 evening. Matches
 * human patience for wait screens (~6-12 update cycles across a
 * typical 30-60s window; 30-60 across the ~5 min story_generating
 * stretch). Slower than useCheckoutPolling (2s, tight webhook window)
 * and useStoryDetail (4s, small resource) but appropriate for a
 * multi-hundred-byte envelope with signed URLs.
 */
const POLL_INTERVAL_MS = 5000;

/**
 * P5-4 Commit A — hook wrapping the book-order preview fetch.
 *
 * House pattern (`pwa/src/features/story/use-story-detail.ts`): the
 * feature-scoped hook consumes the api-layer function, manages loading
 * / error / data state with `useState` + `useEffect`. No TanStack
 * Query (`pwa/CLAUDE.md:64-69`).
 *
 * Error surface: on non-2xx the underlying `apiFetch` throws `ApiError`
 * carrying the full DreamableError envelope. The hook stores the raw
 * `ApiError` in `error` so consumers (P5-4 Commit B re-entry router)
 * can read `error.envelope?.error_code`, `?.ui_action`, `?.cta`, and
 * `?.user_message` directly. Envelope evaluation lives at
 * `pwa/src/features/book/errors.ts`.
 */

export interface UsePreviewEnvelopeState {
  readonly data: PreviewEnvelope | null;
  readonly error: ApiError | null;
  readonly loading: boolean;
  /**
   * Trigger a re-fetch of the current orderId. Called after a
   * mutation that changes the persisted envelope (title save,
   * page retry). Composed hooks (`useReentryStep`) surface this
   * up to the shell so an outlet consumer can request a refresh
   * after a successful edit. No-op when `orderId` is null.
   */
  readonly refetch: () => void;
}

export function usePreviewEnvelope(orderId: string | null): UsePreviewEnvelopeState {
  // Lazy initialiser so the initial state is derived from the first
  // orderId in one shot — avoids a synchronous setState in the effect
  // body (react-hooks/set-state-in-effect). Effect handles subsequent
  // orderId changes via the async fetch callbacks (setState inside a
  // promise callback is not synchronous-in-effect and passes the rule).
  //
  // Consequence: on an orderId change from A → B, state.loading stays
  // false until the new fetch resolves; callers that need a proper
  // "loading" intermediate transition should remount the hook via
  // <Route key={orderId} …> instead.
  const [state, setState] = useState<Omit<UsePreviewEnvelopeState, 'refetch'>>(() => ({
    data: null,
    error: null,
    loading: orderId !== null,
  }));

  // `refetchCounter` triggers a fresh fetch when incremented via
  // `refetch()`. Not exposed to the caller — only the effect reacts.
  const [refetchCounter, setRefetchCounter] = useState(0);

  useEffect(() => {
    if (orderId === null) return;
    let cancelled = false;
    fetchBookOrder(orderId)
      .then((data) => {
        if (cancelled) return;
        setState({ data, error: null, loading: false });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const apiError = err instanceof ApiError ? err : null;
        setState({ data: null, error: apiError, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, refetchCounter]);

  const refetch = useCallback(() => {
    if (orderId === null) return;
    setRefetchCounter((c) => c + 1);
  }, [orderId]);

  // Sebastian ruling 2026-08-12 evening — shared polling primitive
  // composed here. Fires refetch every 5s while the envelope's
  // status is a backend-driven non-terminal state (see
  // NON_TERMINAL_POLLING_STATES). Pauses when the tab is hidden or
  // the browser is offline (default guards in usePolling).
  //
  // shouldPoll evaluates on every render from the current envelope
  // status — as soon as a poll delivers a terminal status
  // (text_editing, anchor_review, preview_ready, ...) the interval
  // tears down on the next render. No manual stop needed.
  //
  // Not called at mount — the fetch-on-mount effect above already
  // does that; usePolling only owns the subsequent ticks.
  usePolling({
    shouldPoll: state.data !== null && NON_TERMINAL_POLLING_STATES.includes(state.data.status),
    onTick: refetch,
    intervalMs: POLL_INTERVAL_MS,
  });

  return { ...state, refetch };
}
